// EveryPay (LHV) payment link page on ws5 pay/context + pay/start (plan Phase 3, T2).
// Since the full cutover (2026-10-14) PaymentRoute.jsx sends every link the parser
// understands here: ?subject=package|order|completed|tip, bare order rec ids
// (`defaulted`, retried once as completed below) and the legacy tips/completed
// suffixes. A not_available refusal for an order, completed order or tip (web rail
// switched off, or a useTmpPrice order) calls onNotAvailable, and PaymentRoute
// renders the legacy Montonio page for that link instead (shouldFallBackToLegacy).
import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '../../i18n';
import CustomerDetailsForm from './CustomerDetailsForm';
import {
    parseOrderIdParam,
    refuseReasonKey,
    isRetryableRefuse,
    subjectHeadingKey,
    formatAmount,
    splitMethods,
    methodLogoUrl,
    effectiveSubject,
} from './paymentSubject';
import { shouldFallBackToLegacy, legacyOrderId } from './paymentLinkSwitch';

// ws1 Default group: contact pre-check for ORDER links only (unchanged, non-Montonio).
const PRE_CHECK_ENDPOINT = 'https://api.wiseclean.ee/api:56Z3lZ-0/old_order/pre_check_client_info';
// ws5 `client` apigroup: public payment-link endpoints (spec §4.1, §4.2).
const PAY_API_BASE = 'https://api.wiseclean.ee/api:nnuLSRT_';
const CONTEXT_ENDPOINT = `${PAY_API_BASE}/pay/context`;
const START_ENDPOINT = `${PAY_API_BASE}/pay/start`;
const SUPPORT_EMAIL = 'info@wiseclean.eu';

// Bounded retry budget (spinner stays up) for: ws5 ok:false `unavailable` (Airtable
// 429/5xx, spec §6) and transient ws1 pre-check / network failures (status 0 or 5xx).
const MAX_RETRIES = 2; // retries after the first attempt (3 total)
const RETRY_DELAY_MS = 2000;
// If assigning window.location.href does not navigate (an email client's in-app
// browser), the tiles must not stay dead forever. Same fallback as BuyPackage.jsx.
const REDIRECT_FALLBACK_MS = 6000;

async function postJson(url, body) {
    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
        let parsed = null;
        try { parsed = await response.json(); } catch (e) { parsed = null; }
        return { ok: response.ok, status: response.status, body: parsed };
    } catch (e) {
        return { ok: false, status: 0, body: null }; // network / DNS / CORS: transient
    }
}

function Header() {
    return (
        <header style={shell.header}>
            <img src="/wiseclean-logo.png" alt="WiseClean" style={shell.logo} />
        </header>
    );
}

function EveryPayPayment({ onNotAvailable = null }) {
    const { lang, orderId } = useParams();
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();

    const activeLang = ['en', 'et'].includes(lang) ? lang : 'en';
    const parsed = useMemo(() => parseOrderIdParam(orderId, searchParams), [orderId, searchParams]);

    const [step, setStep] = useState('checking'); // 'checking' | 'collect' | 'select' | 'refused' | 'error'
    const [context, setContext] = useState(null); // pay/context body + the subject it was resolved with
    const [refuseReason, setRefuseReason] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [notice, setNotice] = useState(null); // inline i18n key under the tiles
    const [reloadTick, setReloadTick] = useState(0); // bumped on amount_changed to re-run context
    const redirectTimerRef = useRef(null);
    // Latest fallback callback without re-running the context effect when the
    // parent re-renders with a new function identity. Only read asynchronously,
    // so it is synced in an effect rather than during render.
    const onNotAvailableRef = useRef(onNotAvailable);
    useEffect(() => {
        onNotAvailableRef.current = onNotAvailable;
    }, [onNotAvailable]);
    // One token per mounted link. handleStart compares it when pay/start answers:
    // an answer that arrives after the page unmounted or moved to another link is
    // dropped, so it can neither set state, redirect nor hand a stale id to the
    // legacy page.
    const linkTokenRef = useRef(null);
    useEffect(() => {
        const token = {};
        linkTokenRef.current = token;
        return () => {
            if (linkTokenRef.current === token) linkTokenRef.current = null;
        };
    }, [parsed]);

    // A not_available refusal for an order, completed order or tip hands the link
    // to the legacy page with its suffix-form id (legacyOrderId). Returns false when
    // there is nothing to hand over (a package, no callback, or no legacy id); the
    // caller then shows the refused screen.
    const handBackToLegacy = (reason, subject, recid) => {
        const fallback = onNotAvailableRef.current;
        if (!fallback || !shouldFallBackToLegacy(reason, subject)) return false;
        const legacyId = legacyOrderId(subject, recid);
        if (!legacyId) return false;
        fallback(legacyId);
        return true;
    };

    useEffect(() => {
        if (['en', 'et'].includes(lang)) {
            i18n.changeLanguage(lang);
        } else {
            // Keep ?subject= (and anything else) when normalising the language segment.
            navigate({ pathname: `/external/payment/en/${orderId}`, search: location.search }, { replace: true });
        }
    }, [lang, orderId, location.search, i18n, navigate]);

    // Back from EveryPay restores this page from the bfcache with `submitting` still true;
    // pageshow fires on that restore. Reset; ws5 reuses the pending attempt (spec §4.2).
    useEffect(() => {
        const onPageShow = (e) => {
            if (!e.persisted) return;
            if (redirectTimerRef.current) clearTimeout(redirectTimerRef.current);
            setSubmitting(false);
        };
        window.addEventListener('pageshow', onPageShow);
        return () => {
            window.removeEventListener('pageshow', onPageShow);
            if (redirectTimerRef.current) clearTimeout(redirectTimerRef.current);
        };
    }, []);

    useEffect(() => {
        if (!parsed) { setStep('error'); return undefined; }
        const { id, defaulted } = parsed;
        let cancelled = false;
        let retryTimer = null;
        const later = (fn) => { retryTimer = setTimeout(fn, RETRY_DELAY_MS); };

        // Order links only: ws1 pre-check that the payer has a name + email on file.
        // hasUser:false is permanent (orphan order without UserID); 5xx and network
        // failures are transient. After the retries: fail closed, "contact support"
        // beats a payment with an empty payer block.
        const runPreCheck = async (attempt) => {
            let res;
            try {
                const r = await fetch(`${PRE_CHECK_ENDPOINT}?orderID=${encodeURIComponent(id)}`);
                res = r.ok ? { ok: true, data: await r.json() } : { ok: false, status: r.status };
            } catch (e) {
                res = { ok: false, status: 0 };
            }
            if (cancelled) return;
            if (res.ok) {
                if (!res.data || res.data.hasUser === false) {
                    setStep('error');
                    return;
                }
                setStep(res.data.needsInput ? 'collect' : 'select');
                return;
            }
            if ((res.status === 0 || res.status >= 500) && attempt < MAX_RETRIES) {
                later(() => runPreCheck(attempt + 1));
                return;
            }
            setStep('error');
        };

        // `attempt` counts transient retries for one subject. A DEFAULTED link
        // (bare rec id, no ?subject=) that the table-scoped resolver answers
        // not_found for as `order` is tried ONCE as `completed`: admin links for
        // completed orders carry a bare rec id (spec §4.8). No further rounds.
        const loadContext = async (subject, attempt) => {
            const res = await postJson(CONTEXT_ENDPOINT, { subject, id, locale: activeLang });
            if (cancelled) return;
            const body = res.body && typeof res.body === 'object' ? res.body : null;
            if (!res.ok || !body) {
                if ((res.status === 0 || res.status >= 500) && attempt < MAX_RETRIES) {
                    later(() => loadContext(subject, attempt + 1));
                    return;
                }
                setStep('error');
                return;
            }
            if (body.ok === false) {
                const reason = String(body.refuse_reason || '');
                if (isRetryableRefuse(reason) && attempt < MAX_RETRIES) {
                    later(() => loadContext(subject, attempt + 1));
                    return;
                }
                if (reason === 'not_found' && defaulted && subject === 'order') {
                    loadContext('completed', 0);
                    return;
                }
                if (handBackToLegacy(reason, subject, id)) return;
                setRefuseReason(reason);
                setStep('refused');
                return;
            }
            // Only a well-formed success may reach the tiles: an HTTP 200 that is not
            // {ok:true, amount_eur > 0} (a Xano error object, a proxy page) would
            // otherwise render a pay screen with no amount and skip the server's
            // amount_changed guard. A package also needs its rec id for pay/start.
            const amount = Number(body.amount_eur);
            const hasRecid = typeof body.recid === 'string' && body.recid.length > 0;
            if (body.ok !== true || !Number.isFinite(amount) || amount <= 0
                || (subject === 'package' && !hasRecid)) {
                setStep('error');
                return;
            }
            // A finished order comes back as its archive (effective_subject
            // "completed" + the archive recid): pay that pair, and skip the
            // order-only pre-check, which reads the deleted Orders row.
            const paySubject = effectiveSubject(subject, body);
            setContext({ ...body, subject: paySubject });
            if (paySubject === 'order') {
                runPreCheck(0);
            } else {
                setStep('select');
            }
        };

        setStep('checking');
        setContext(null);
        loadContext(parsed.subject, 0);

        return () => {
            cancelled = true;
            if (retryTimer) clearTimeout(retryTimer);
        };
    }, [parsed, activeLang, reloadTick]);

    // Create (or reuse) the EveryPay payment and leave the page. `methodSource` = the opaque
    // EveryPay `source` of a tile, '' = hosted page (wallets, hosted_only). pay/start takes
    // the rec id only, so a package link (addressed by Nr) sends context.recid.
    const handleStart = (methodSource) => {
        if (submitting || !parsed || !context) return;
        const recid = typeof context.recid === 'string' && context.recid
            ? context.recid
            : (context.subject !== 'package' ? parsed.id : '');
        if (!recid) { setNotice('checkoutFailed'); return; }
        const token = linkTokenRef.current;
        setSubmitting(true);
        setNotice(null);
        postJson(START_ENDPOINT, {
            subject: context.subject,
            id: recid,
            locale: activeLang,
            method_source: methodSource || '',
            shown_amount_eur: context.amount_eur,
        }).then((res) => {
            // Unmounted, or moved to another link while pay/start ran: this answer
            // belongs to a link that is no longer on screen.
            if (!token || linkTokenRef.current !== token) return;
            const body = res.body && typeof res.body === 'object' ? res.body : null;
            // ws5 refusal (HTTP 200, {ok:false, refuse_reason}): amount_changed re-runs
            // context; the transient `unavailable` stays inline with the tiles enabled;
            // everything else lands on the same refused screen as a context refusal.
            if (res.ok && body && body.ok === false) {
                const reason = String(body.refuse_reason || '');
                setSubmitting(false);
                if (reason === 'amount_changed') {
                    setNotice('payAmountChanged');
                    setReloadTick((n) => n + 1);
                    return;
                }
                if (isRetryableRefuse(reason)) {
                    setNotice('payMethodsUnavailable');
                    return;
                }
                // Same subject + rec id pair pay/start was just sent: a finished
                // order resolved to its archive falls back as <archive>completed.
                if (handBackToLegacy(reason, context.subject, recid)) return;
                setRefuseReason(reason);
                setStep('refused');
                return;
            }
            const paymentUrl = res.ok && body && typeof body.payment_url === 'string'
                && body.payment_url.startsWith('https://')
                ? body.payment_url
                : '';
            if (!paymentUrl) {
                // HTTP 500 = EveryPay oneoff failed; no attempt row exists then (spec §6).
                setNotice('checkoutFailed');
                setSubmitting(false);
                return;
            }
            window.location.href = paymentUrl;
            redirectTimerRef.current = setTimeout(() => setSubmitting(false), REDIRECT_FALLBACK_MS);
        });
    };

    if (step === 'checking') {
        return (
            <div style={shell.page}>
                <style>{`
                    @keyframes wc-payment-spin { to { transform: rotate(360deg); } }
                    @media (prefers-reduced-motion: reduce) { .wc-payment-spinner { animation-duration: 2400ms; } }
                `}</style>
                <Header />
                <main style={shell.centered}>
                    <div className="wc-payment-spinner" style={shell.spinner} aria-hidden="true" />
                    <div style={shell.muted} role="status" aria-live="polite">{t('paymentPreparing')}</div>
                </main>
            </div>
        );
    }

    if (step === 'error' || step === 'refused') {
        const isRefused = step === 'refused';
        return (
            <div style={shell.page}>
                <Header />
                <main style={shell.top}>
                    <div style={shell.card}>
                        <h1 style={shell.title}>{isRefused ? t('refuseTitle') : t('criticalErrorTitle')}</h1>
                        <p style={shell.body}>
                            {isRefused ? t(refuseReasonKey(refuseReason)) : t('criticalErrorBody')}
                        </p>
                        <a href={`mailto:${SUPPORT_EMAIL}`} style={shell.link}>
                            {t('contactSupport')}: {SUPPORT_EMAIL}
                        </a>
                    </div>
                </main>
            </div>
        );
    }

    if (step === 'collect') {
        return (
            <CustomerDetailsForm orderId={parsed.id} onSaved={() => setStep('select')} onCritical={() => setStep('error')} />
        );
    }

    const { banks, card } = splitMethods(context && context.methods);
    const wallets = (context && context.wallets) || {};
    const hasWallet = wallets.apple === true || wallets.google === true;
    // hosted_only comes from ws5 when processing_accounts is down; an empty method
    // list without the flag is treated the same way so the customer can still pay.
    const hostedOnly = (context && context.hosted_only === true) || (banks.length === 0 && !card);
    const disabledStyle = submitting ? select.disabled : null;

    return (
        <div style={shell.page}>
            <Header />
            <main style={select.content}>
                <section style={shell.card} aria-live="polite">
                    <h1 style={select.heading}>
                        {t(subjectHeadingKey(context.subject), { label: context.label || '' })}
                        {' · '}
                        {formatAmount(context.amount_eur)}
                    </h1>
                </section>

                {hostedOnly && (
                    <section style={select.section}>
                        <button type="button" onClick={() => handleStart('')} disabled={submitting} style={{ ...select.cta, ...disabledStyle }}>
                            {t('payHostedTile')}
                        </button>
                    </section>
                )}

                {!hostedOnly && banks.length > 0 && (
                    <section style={select.section}>
                        <h2 style={select.sectionTitle}>
                            {t('payViaBank')}{' '}
                            <span style={select.badge}>{t('smartChoice')}</span>
                        </h2>
                        <div style={select.tileGrid}>
                            {banks.map((method) => (
                                <button
                                    key={method.source} type="button" disabled={submitting}
                                    onClick={() => handleStart(method.source)}
                                    aria-label={method.display_name || method.source}
                                    style={{ ...select.tile, ...disabledStyle }}
                                >
                                    <MethodLogo method={method} />
                                </button>
                            ))}
                        </div>
                    </section>
                )}

                {!hostedOnly && card && (
                    <section style={select.section}>
                        <h2 style={select.sectionTitle}>{t('payViaCard')}</h2>
                        <button
                            type="button" disabled={submitting} onClick={() => handleStart(card.source)}
                            aria-label={card.display_name || t('payViaCard')}
                            style={{ ...select.tile, ...disabledStyle }}
                        >
                            <MethodLogo method={card} />
                        </button>
                    </section>
                )}

                {!hostedOnly && hasWallet && (
                    <section style={select.section}>
                        <button type="button" onClick={() => handleStart('')} disabled={submitting} style={{ ...select.cta, ...disabledStyle }}>
                            {t('payWalletHint')}
                        </button>
                        <p style={select.note}>{t('payWalletNote')}</p>
                    </section>
                )}

                {notice && <p style={select.errorText} role="alert">{t(notice)}</p>}
                {submitting && (
                    <p style={shell.muted} role="status" aria-live="polite">{t('checkoutStarting')}</p>
                )}
            </main>
        </div>
    );
}

// EveryPay-hosted logo (https only), or the method name as text when EveryPay sent
// no logo or the remote file fails to load. No bundled artwork.
function MethodLogo({ method }) {
    const [failed, setFailed] = useState(false);
    const src = failed ? '' : methodLogoUrl(method);
    if (!src) {
        return <span style={select.tileText}>{method.display_name || method.source}</span>;
    }
    return (
        <img src={src} alt={method.display_name || method.source} style={select.tileImage} onError={() => setFailed(true)} />
    );
}

EveryPayPayment.propTypes = {
    // Called with the legacy-form id (paymentLinkSwitch.legacyOrderId) instead of
    // the refused screen when ws5 answers not_available for an order, completed
    // order or tip; PaymentRoute then renders the legacy page with that id.
    onNotAvailable: PropTypes.func,
};

MethodLogo.propTypes = {
    method: PropTypes.shape({
        source: PropTypes.string,
        display_name: PropTypes.string,
        logo_url: PropTypes.string,
    }).isRequired,
};

const shell = {
    page: { minHeight: '100vh', background: 'var(--wc-bg-secondary)', display: 'flex', flexDirection: 'column' },
    header: { padding: '20px 24px', display: 'flex', justifyContent: 'center' },
    logo: { height: '36px', width: 'auto', objectFit: 'contain' },
    centered: { flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '20px' },
    top: { flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: '8px 20px 40px' },
    spinner: {
        width: '40px', height: '40px', borderRadius: '50%', marginBottom: '16px',
        border: '3px solid var(--wc-brand-light)', borderTopColor: 'var(--wc-brand)',
        animation: 'wc-payment-spin 800ms linear infinite',
    },
    muted: { color: 'var(--wc-text-secondary)', fontSize: '15px', textAlign: 'center', margin: 0 },
    card: {
        width: '100%', maxWidth: '460px', boxSizing: 'border-box', margin: '0 auto 28px', padding: '24px', textAlign: 'center',
        background: 'var(--wc-bg-primary)', border: '1px solid var(--wc-border)',
        borderRadius: 'var(--wc-radius-card)', boxShadow: 'var(--wc-shadow-card)',
    },
    title: { fontSize: '22px', fontWeight: 700, margin: '0 0 10px 0', color: 'var(--wc-text-primary)' },
    body: { fontSize: '15px', color: 'var(--wc-text-secondary)', lineHeight: 1.6, margin: '0 0 20px 0' },
    link: { display: 'inline-block', color: 'var(--wc-brand-dark)', fontWeight: 600, textDecoration: 'none', wordBreak: 'break-word' },
};

const select = {
    content: { padding: '0 20px 40px', maxWidth: '500px', margin: '0 auto', color: 'var(--wc-text-primary)' },
    heading: { fontSize: '24px', lineHeight: 1.2, fontWeight: 700, margin: 0, color: 'var(--wc-text-primary)' },
    section: { marginBottom: '30px' },
    sectionTitle: { fontSize: '20px', marginBottom: '10px', fontWeight: 700, color: 'var(--wc-text-primary)' },
    badge: {
        color: 'var(--wc-text-primary)', fontWeight: 600, fontSize: '13px', verticalAlign: 'middle',
        backgroundColor: 'var(--wc-brand-subtle)', padding: '3px 8px', borderRadius: '20px',
    },
    // EveryPay ships every method logo as a wide 160x46 SVG, so the tiles are wide
    // too: two per row on a phone (from a 320px screen: 280px content fits 2 x 128
    // + gap), three on a wider screen. A square 72px tile shrank the logos to
    // about 60x17 px.
    tileGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(128px, 1fr))', gap: '10px' },
    tile: {
        width: '100%', height: '64px', padding: '8px 12px', cursor: 'pointer', fontFamily: 'inherit',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        border: '1px solid var(--wc-border)', borderRadius: 'var(--wc-radius-btn)',
        backgroundColor: 'var(--wc-bg-primary)', boxShadow: 'var(--wc-shadow-card)',
        transition: 'border-color var(--wc-dur-fast) var(--wc-ease), box-shadow var(--wc-dur-fast) var(--wc-ease)',
    },
    disabled: { opacity: 0.5, cursor: 'not-allowed', boxShadow: 'none' },
    tileImage: { width: '100%', height: '100%', objectFit: 'contain', display: 'block' },
    tileText: { fontSize: '12px', fontWeight: 600, color: 'var(--wc-text-primary)', textAlign: 'center' },
    cta: {
        width: '100%', padding: '14px 16px', minHeight: '48px', fontSize: '16px', fontWeight: 600, fontFamily: 'inherit',
        color: 'var(--wc-on-brand)', background: 'var(--wc-brand)', border: 'none', cursor: 'pointer',
        borderRadius: 'var(--wc-radius-btn)', boxShadow: 'var(--wc-shadow-cta)',
    },
    note: { fontSize: '13px', color: 'var(--wc-text-secondary)', textAlign: 'center', margin: '8px 0 0' },
    errorText: { fontSize: '14px', color: 'var(--wc-error)', textAlign: 'center', margin: '0 0 12px' },
};

export default EveryPayPayment;
